import { useInfiniteQuery } from '@tanstack/react-query'
import NProgress from 'nprogress'
import { tmdb, iziplaySearch, iziplaySocial } from '../tools/api'
import { useLang } from '../state/ui'
import { queryKeys } from './keys'

/*
 * The four paginated lists the app scrolls through. They were four near-identical
 * mobx slices holding a page counter, an accumulating array and a reset method;
 * `useInfiniteQuery` is exactly that shape, so the accumulation, the reset on
 * language change and the de-duplication of in-flight requests all come for free.
 */

interface TmdbPage<T> {
  page: number
  results: T[]
  total_pages: number
  total_results: number
}

function discover<T>(kind: 'movie' | 'tv', lang: string, page: number) {
  return tmdb
    .get<TmdbPage<T>>(`discover/${kind}?language=${lang}&page=${page}&sort_by=popularity.desc`)
    .then(res => res.data)
}

export function useMovies() {
  const lang = useLang()

  return useInfiniteQuery({
    queryKey: queryKeys.movies(lang),
    queryFn: ({ pageParam }) => discover<TMDB.Movie>('movie', lang, pageParam),
    initialPageParam: 1,
    getNextPageParam: last => (last.page < last.total_pages ? last.page + 1 : undefined),
    select: data => data.pages.flatMap(page => page.results)
  })
}

export function useTvShows() {
  const lang = useLang()

  return useInfiniteQuery({
    queryKey: queryKeys.tvshows(lang),
    queryFn: ({ pageParam }) => discover<TMDB.TvShow>('tv', lang, pageParam),
    initialPageParam: 1,
    getNextPageParam: last => (last.page < last.total_pages ? last.page + 1 : undefined),
    select: data => data.pages.flatMap(page => page.results)
  })
}

/*
 * Downloads and history are iziplay rows carrying an imdb id, which then has to
 * be resolved against TMDB for artwork and titles. The old code walked those
 * with `async.eachOf` and a callback; `Promise.all` over the page does the same
 * work in parallel without the extra dependency, and a failed lookup drops the
 * row rather than rejecting the page.
 */
async function withTmdbInfo<T extends Iziplay.Downloaded>(
  rows: T[],
  lang: string
): Promise<T[]> {
  const resolved = await Promise.all(
    rows.map(async row => {
      if (!row.imdb) return null

      const imdb = row.imdb.startsWith('tt') ? row.imdb : `tt${row.imdb}`

      try {
        const found = await tmdb.get<any>(`find/${imdb}?language=${lang}&external_source=imdb_id`)
        NProgress.inc()

        const match = found.data?.movie_results?.[0] || found.data?.tv_results?.[0] || {}
        const merged: any = { ...row, ...match, file: row.id }

        if (row.season !== null && row.season !== undefined && match.id) {
          const episode = await tmdb.get<any>(
            `tv/${match.id}/season/${row.season}/episode/${row.episode}`
          )
          Object.assign(merged, episode.data)
          merged.file = row.id
          merged.id_episode = merged.id
          merged.id = match.id
        }

        return merged as T
      } catch {
        return null
      }
    })
  )

  return resolved.filter((row): row is Awaited<T> => row !== null)
}

export function useDownloaded() {
  const lang = useLang()

  return useInfiniteQuery({
    queryKey: queryKeys.downloaded(lang),
    queryFn: async ({ pageParam }) => {
      const res = await iziplaySearch().get<{
        downloaded: Iziplay.Downloaded[]
        total: number
      }>(`/downloaded?page=${pageParam}`)

      return {
        rows: await withTmdbInfo(res.data.downloaded, lang),
        page: pageParam,
        total: res.data.total
      }
    },
    initialPageParam: 1,
    getNextPageParam: last => (last.rows.length === 0 ? undefined : last.page + 1),
    select: data =>
      data.pages.flatMap(page => page.rows).sort((a, b) => b.downloaded - a.downloaded)
  })
}

export function useMyHistory() {
  const lang = useLang()

  return useInfiniteQuery({
    queryKey: queryKeys.myhistory(lang),
    queryFn: async ({ pageParam }) => {
      const res = await iziplaySocial().get<{
        data: Iziplay.MyHistory[]
        total: number
      }>(`/history?page=${pageParam}`)

      return {
        rows: await withTmdbInfo(res.data.data, lang),
        page: pageParam,
        total: res.data.total
      }
    },
    initialPageParam: 1,
    getNextPageParam: last => (last.rows.length === 0 ? undefined : last.page + 1),
    select: data => data.pages.flatMap(page => page.rows).sort((a, b) => b.updated - a.updated)
  })
}
